import * as THREE from 'three';

const PANEL_CANVAS_WIDTH = 1280;

function roundedRect(context, x, y, width, height, radius) {
  const safeRadius = Math.min(radius, width * 0.5, height * 0.5);
  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.lineTo(x + width - safeRadius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  context.lineTo(x + width, y + height - safeRadius);
  context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  context.lineTo(x + safeRadius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  context.lineTo(x, y + safeRadius);
  context.quadraticCurveTo(x, y, x + safeRadius, y);
  context.closePath();
}

/**
 * Canvas-backed panel that stays entirely inside the Three.js scene.
 * Button rectangles are transparent meshes in front of the canvas surface.
 */
export class XRPanel extends THREE.Group {
  constructor({
    width = 1.32,
    height = 0.82,
    distance = 1.7,
    canvasWidth = PANEL_CANVAS_WIDTH,
    onAction = null,
  } = {}) {
    super();

    this.name = 'xr-panel';
    this.panelWidth = width;
    this.panelHeight = height;
    this.distance = distance;
    this.onAction = onAction;
    this.isOpen = false;

    this.canvas = document.createElement('canvas');
    this.canvas.width = canvasWidth;
    this.canvas.height = Math.round(canvasWidth * (height / width));
    this.context = this.canvas.getContext('2d');

    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.minFilter = THREE.LinearMipmapLinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.generateMipmaps = true;

    this.panelMaterial = new THREE.MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      side: THREE.DoubleSide,
    });
    this.panelMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(this.panelWidth, this.panelHeight),
      this.panelMaterial,
    );
    this.panelMesh.name = 'xr-panel:surface';
    this.panelMesh.renderOrder = 100;
    this.add(this.panelMesh);

    this._buttonGeometry = new THREE.PlaneGeometry(1, 1);
    this._buttonHitMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      colorWrite: false,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.buttons = [];
    this._interactiveMeshes = [];
    this.hoveredAction = null;

    this._cameraWorldPosition = new THREE.Vector3();
    this._cameraWorldDirection = new THREE.Vector3();
    this._cameraWorldQuaternion = new THREE.Quaternion();
    this._parentWorldQuaternion = new THREE.Quaternion();

    this.visible = false;
  }

  setButtons(buttons) {
    for (let index = 0; index < this.buttons.length; index += 1) {
      this.buttons[index].mesh.removeFromParent();
    }

    this.buttons.length = 0;
    this._interactiveMeshes.length = 0;

    for (let index = 0; index < buttons.length; index += 1) {
      const definition = buttons[index];
      const button = {
        action: definition.action,
        label: definition.label,
        x: definition.x,
        y: definition.y,
        width: definition.width,
        height: definition.height,
        enabled: definition.enabled !== false,
        style: definition.style ?? 'default',
        mesh: null,
      };

      const mesh = new THREE.Mesh(this._buttonGeometry, this._buttonHitMaterial);
      mesh.name = `xr-panel:button:${button.action}`;
      mesh.position.set(
        (button.x + button.width * 0.5 - 0.5) * this.panelWidth,
        (0.5 - button.y - button.height * 0.5) * this.panelHeight,
        0.012,
      );
      mesh.scale.set(
        button.width * this.panelWidth,
        button.height * this.panelHeight,
        1,
      );
      mesh.renderOrder = 101;
      mesh.userData.interactive = true;
      mesh.userData.interactionType = 'xr-ui';
      mesh.userData.xrAction = button.action;
      mesh.userData.xrPanel = this;
      button.mesh = mesh;

      this.buttons.push(button);
      this.add(mesh);
    }

    this._refreshInteractiveMeshes();
    this.redraw();
    return this;
  }

  setButtonEnabled(action, enabled) {
    const button = this._findButton(action);
    if (!button || button.enabled === enabled) {
      return;
    }

    button.enabled = enabled;
    if (!enabled && this.hoveredAction === action) {
      this.hoveredAction = null;
    }
    this._refreshInteractiveMeshes();
    this.redraw();
  }

  isActionEnabled(action) {
    return this._findButton(action)?.enabled === true;
  }

  _findButton(action) {
    for (let index = 0; index < this.buttons.length; index += 1) {
      if (this.buttons[index].action === action) {
        return this.buttons[index];
      }
    }
    return null;
  }

  _refreshInteractiveMeshes() {
    this._interactiveMeshes.length = 0;
    for (let index = 0; index < this.buttons.length; index += 1) {
      const button = this.buttons[index];
      if (button.enabled) {
        this._interactiveMeshes.push(button.mesh);
      }
    }
  }

  getInteractiveMeshes() {
    return this._interactiveMeshes;
  }

  getActionFromIntersection(intersection) {
    return intersection?.object?.userData?.xrAction ?? null;
  }

  setHoveredAction(action) {
    const nextAction = this.isActionEnabled(action) ? action : null;
    if (this.hoveredAction === nextAction) {
      return;
    }

    this.hoveredAction = nextAction;
    this.redraw();
  }

  placeInFrontOf(camera, distance = this.distance) {
    if (!camera) {
      return;
    }

    camera.getWorldPosition(this._cameraWorldPosition);
    camera.getWorldDirection(this._cameraWorldDirection);
    this._cameraWorldPosition.addScaledVector(this._cameraWorldDirection, distance);

    this.position.copy(this._cameraWorldPosition);
    if (this.parent) {
      this.parent.worldToLocal(this.position);
    }

    this.faceCamera(camera);
    this.updateMatrixWorld(true);
  }

  faceCamera(camera) {
    if (!camera) {
      return;
    }

    camera.getWorldQuaternion(this._cameraWorldQuaternion);
    if (this.parent) {
      this.parent.getWorldQuaternion(this._parentWorldQuaternion).invert();
      this.quaternion
        .copy(this._parentWorldQuaternion)
        .multiply(this._cameraWorldQuaternion);
    } else {
      this.quaternion.copy(this._cameraWorldQuaternion);
    }
  }

  show(camera) {
    this.visible = true;
    this.isOpen = true;
    this.placeInFrontOf(camera);
    return this;
  }

  hide() {
    this.visible = false;
    this.isOpen = false;
    this.setHoveredAction(null);
    return this;
  }

  update(camera) {
    if (this.isOpen) {
      this.faceCamera(camera);
    }
  }

  handleAction(action) {
    if (!this.isActionEnabled(action)) {
      return false;
    }

    this.onAction?.(action, this);
    return true;
  }

  redraw() {
    const context = this.context;
    const width = this.canvas.width;
    const height = this.canvas.height;

    context.clearRect(0, 0, width, height);
    context.fillStyle = 'rgba(7, 25, 30, 0.97)';
    roundedRect(context, 3, 3, width - 6, height - 6, 44);
    context.fill();

    context.strokeStyle = 'rgba(243, 201, 107, 0.94)';
    context.lineWidth = 5;
    context.stroke();

    this.drawContent(context, width, height);
    this._drawButtons(context, width, height);
    this.texture.needsUpdate = true;
  }

  drawContent() {}

  _drawButtons(context, canvasWidth, canvasHeight) {
    for (let index = 0; index < this.buttons.length; index += 1) {
      const button = this.buttons[index];
      const x = button.x * canvasWidth;
      const y = button.y * canvasHeight;
      const width = button.width * canvasWidth;
      const height = button.height * canvasHeight;
      const hovered = this.hoveredAction === button.action;

      if (!button.enabled) {
        context.fillStyle = 'rgba(113, 127, 130, 0.2)';
        context.strokeStyle = 'rgba(175, 185, 187, 0.26)';
      } else if (hovered) {
        context.fillStyle = '#f3c96b';
        context.strokeStyle = '#fff4d2';
      } else if (button.style === 'primary') {
        context.fillStyle = 'rgba(39, 105, 115, 0.95)';
        context.strokeStyle = 'rgba(116, 208, 216, 0.9)';
      } else {
        context.fillStyle = 'rgba(255, 255, 255, 0.08)';
        context.strokeStyle = 'rgba(255, 255, 255, 0.36)';
      }

      roundedRect(context, x, y, width, height, 18);
      context.fill();
      context.lineWidth = hovered ? 5 : 3;
      context.stroke();

      context.fillStyle = !button.enabled
        ? 'rgba(220, 226, 227, 0.42)'
        : hovered
          ? '#102f36'
          : '#fffaf0';
      context.font = `700 ${Math.round(canvasHeight * 0.032)}px system-ui, -apple-system, sans-serif`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(button.label, x + width * 0.5, y + height * 0.52);
    }
  }

  dispose() {
    for (let index = 0; index < this.buttons.length; index += 1) {
      this.buttons[index].mesh.removeFromParent();
    }
    this.buttons.length = 0;
    this._interactiveMeshes.length = 0;
    this.panelMesh.geometry.dispose();
    this.panelMaterial.dispose();
    this.texture.dispose();
    this._buttonGeometry.dispose();
    this._buttonHitMaterial.dispose();
    this.removeFromParent();
  }
}

export default XRPanel;
